'use strict';

var React = require('react');
require('../../theme/breakpoints.js');
require('imask');
var useIsomorphicLayoutEffect = require('../../hooks/useIsomorphicLayoutEffect.js');
var useResizeObserver = require('../../hooks/useResizeObserver.js');
var useScrollListener = require('../../hooks/useScrollListener.js');
var cssUnits = require('../../helpers/css-units.js');

const getOffsetTopOfSide = (key) => (element) => window.scrollY + element.getBoundingClientRect()[key];
const getOffsetLeftOfSide = (key) => (element) => window.scrollX + element.getBoundingClientRect()[key];
const getAlignLeftPositionX = ({ overlay, target, documentWidth }) => {
    const targetLeft = getOffsetLeftOfSide('left')(target);
    const freeWidth = documentWidth - targetLeft;
    const shouldMoveToTheLeft = overlay.clientWidth > freeWidth;
    return shouldMoveToTheLeft ? targetLeft - (overlay.clientWidth - freeWidth) : targetLeft;
};
const getFitContentPositionX = ({ overlay, target, documentWidth }) => {
    const targetLeft = getOffsetLeftOfSide('left')(target);
    const targetWidth = target.clientWidth;
    const overlayWidth = overlay.clientWidth;
    // Try to center the overlay on the target
    const centeredPosition = targetLeft + (targetWidth - overlayWidth) / 2;
    // Ensure overlay doesn't go off-screen
    const minPosition = 0;
    const maxPosition = documentWidth - overlayWidth;
    return Math.max(minPosition, Math.min(centeredPosition, maxPosition));
};
const getOpenedPositionX = ({ overlay, target, variant, documentWidth }) => {
    switch (variant) {
        case 'align-left':
            return getAlignLeftPositionX({ overlay, target, documentWidth });
        case 'fit-target':
            return getOffsetLeftOfSide('left')(target);
        case 'align-right':
            return getOffsetLeftOfSide('right')(target) - overlay.clientWidth;
        case 'fit-content':
            // For fit-content, center the overlay on the target but respect viewport bounds
            return getFitContentPositionX({ overlay, target, documentWidth });
    }
};
const usePositionX = ({ overlayRef, targetRef, isOpened, variant }) => {
    const [positionX, setPositionX] = React.useState(null);
    const setComputedPositionX = React.useCallback(() => {
        const target = targetRef.current;
        const overlay = overlayRef.current;
        if (!isOpened || !target || !overlay) {
            setPositionX(null);
            return;
        }
        setPositionX(getOpenedPositionX({
            overlay,
            target,
            variant,
            documentWidth: globalThis.document?.body?.clientWidth ?? 0,
        }));
    }, [overlayRef, targetRef, isOpened, variant]);
    useIsomorphicLayoutEffect.useIsomorphicLayoutEffect(setComputedPositionX, [setComputedPositionX]);
    useScrollListener.useScrollListener({ elementRef: targetRef, listener: setComputedPositionX });
    useResizeObserver.useResizeObserver({
        elementRefs: [targetRef, overlayRef],
        observer: setComputedPositionX,
    });
    return positionX;
};
const getOpenedPositionY = ({ overlay, target, documentHeight, overlaySpaceToTarget }) => {
    const spaceBetweenTargetAndOverlayInPx = cssUnits.remToPx(overlaySpaceToTarget);
    const neededHeight = overlay.clientHeight + spaceBetweenTargetAndOverlayInPx;
    const targetBottom = getOffsetTopOfSide('bottom')(target);
    const freeHeight = documentHeight - targetBottom;
    const shouldPlaceAtBottom = neededHeight < freeHeight;
    return shouldPlaceAtBottom
        ? targetBottom + spaceBetweenTargetAndOverlayInPx
        : getOffsetTopOfSide('top')(target) - spaceBetweenTargetAndOverlayInPx - overlay.clientHeight;
};
const usePositionY = ({ overlayRef, targetRef, isOpened, overlaySpaceToTarget, }) => {
    const [positionY, setPositionY] = React.useState(null);
    const setComputedPositionY = React.useCallback(() => {
        const overlay = overlayRef.current;
        const target = targetRef.current;
        if (!isOpened || !overlay || !target) {
            setPositionY(null);
            return;
        }
        setPositionY(getOpenedPositionY({
            target,
            overlay,
            overlaySpaceToTarget,
            documentHeight: globalThis.document?.body?.clientHeight ?? 0,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlayRef, targetRef, isOpened]);
    useIsomorphicLayoutEffect.useIsomorphicLayoutEffect(setComputedPositionY, [setComputedPositionY]);
    useScrollListener.useScrollListener({ elementRef: targetRef, listener: setComputedPositionY });
    useResizeObserver.useResizeObserver({
        elementRefs: [targetRef, overlayRef, React.useRef(globalThis.document?.body)],
        observer: setComputedPositionY,
    });
    return positionY;
};
const getMaxWidth = ({ target, variant, documentWidth, overlaySpaceToTarget }) => {
    const paddingFromScreenBorderInPx = cssUnits.remToPx(overlaySpaceToTarget);
    switch (variant) {
        case 'fit-target':
            return target.clientWidth;
        case 'align-right':
            return getOffsetLeftOfSide('right')(target) - paddingFromScreenBorderInPx;
        case 'align-left':
            return documentWidth - paddingFromScreenBorderInPx;
        case 'fit-content':
            // For fit-content, return the maximum possible width (full viewport minus padding)
            // This acts as a constraint, but the actual width will be determined by content
            return documentWidth - paddingFromScreenBorderInPx * 2;
    }
};
const useMaxWidth = ({ targetRef, isOpened, variant, overlaySpaceToTarget, }) => {
    const [maxWidth, setMaxWidth] = React.useState(null);
    const setComputedMaxWidth = React.useCallback(() => {
        const target = targetRef.current;
        if (!isOpened || !target) {
            setMaxWidth(null);
            return;
        }
        setMaxWidth(getMaxWidth({
            target,
            variant,
            overlaySpaceToTarget,
            documentWidth: globalThis.document?.body?.clientWidth ?? 0,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetRef, isOpened, variant]);
    useIsomorphicLayoutEffect.useIsomorphicLayoutEffect(setComputedMaxWidth, [setComputedMaxWidth]);
    useScrollListener.useScrollListener({ elementRef: targetRef, listener: setComputedMaxWidth });
    useResizeObserver.useResizeObserver({
        elementRefs: [targetRef, React.useRef(globalThis.document?.body)],
        observer: setComputedMaxWidth,
    });
    return maxWidth;
};

exports.useMaxWidth = useMaxWidth;
exports.usePositionX = usePositionX;
exports.usePositionY = usePositionY;
//# sourceMappingURL=helpers.js.map
